import queryString, { OutputParams } from 'query-string';
import { createContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';

import { StoreI } from '~src/rootReducer';
import { ApiReduxResp } from '~src/services/apiMiddleware';
import Pagination from '~src/shared/lists/pagination/Pagination';
import LoadingSpinner from '~src/shared/loadingSpinner/LoadingSpinner';
import ResetFiltersAction from '~src/shared/ResetFiltersAction';
import { fetchObjectivesA } from '~src/store/okrs/actions';
import { ObjectivesQueryI, ObjectivesResp, queryParamsToObjectivesQuery } from '~src/store/okrs/interfaces';
import { OkrsSessionState } from '~src/store/okrs/objective.reducer';
import { MainContainerAnimation } from '~src/styles/animations';

import Header from './header';
import ObjectiveRow from './ObjectiveRow';
import { EmptyMessage, ListContainer } from './objectivesOverview.sc';

interface StateProps {
  loadingObjectives: OkrsSessionState['loadingObjectives'];
  objectivesListData: OkrsSessionState['objectivesListData'];
}

interface DispatchProps {
  fetchObjectives(queryObj?: ObjectivesQueryI): ApiReduxResp<ObjectivesResp>;
}

type Props = StateProps & DispatchProps & RouteComponentProps;

export const FiltersContext = createContext<{
  filterObj: ObjectivesQueryI;
  filterObjectives(queryObj?: ObjectivesQueryI): Promise<void>;
}>({
  filterObj: {},
  filterObjectives: (/*_?: ObjectivesQueryI*/) => Promise.reject('uninitialized!'),
});

const ObjectivesOverview = (props: Props) => {
  const [t] = useTranslation();
  const [filterObj, setFilterObj] = useState<ObjectivesQueryI>(() => {
    const queryObj: OutputParams = queryString.parse(props.location.search);
    return queryParamsToObjectivesQuery(queryObj);
  });
  const [loadingFilteredPage, setLoadingFilteredPage] = useState(false);

  useEffect(() => {
    void getObjectives(filterObj);
  }, []);

  const getObjectives = async (queryObj?: ObjectivesQueryI) => {
    setFilterObj((state) => (queryObj ? { ...state, ...queryObj } : {}));
    const newFilterObj = queryObj ? { ...filterObj, ...queryObj } : {};
    props.history.replace({ search: queryString.stringify(newFilterObj) });
    await props.fetchObjectives(newFilterObj);
  };

  const filterObjectives = async (queryObj?: ObjectivesQueryI) => {
    setLoadingFilteredPage(true);
    await getObjectives(queryObj);
    setLoadingFilteredPage(false);
  };

  const getObjectivesOnPage = (page: number) => void getObjectives({ page });

  const resetFilters = (/*_: React.MouseEvent<HTMLButtonElement, MouseEvent>*/) => void filterObjectives();

  return (
    <>
      <FiltersContext.Provider value={{ filterObjectives, filterObj }}>
        <Header isNoObjectives={!props.objectivesListData || !props.objectivesListData.items.length} />
      </FiltersContext.Provider>
      <ListContainer>
        {props.loadingObjectives ? (
          <LoadingSpinner marginTop={60} />
        ) : (
          props.objectivesListData && (
            <MainContainerAnimation>
              {props.objectivesListData.items.length ? (
                props.objectivesListData.items.map((item) => <ObjectiveRow key={item.id} itemData={item} />)
              ) : Object.keys(filterObj).length > 1 ? (
                <ResetFiltersAction clickFn={resetFilters} />
              ) : (
                <EmptyMessage>
                  <p className="hl3">{t('x.okr.objectives.no_items_title')}</p>
                  <p>{t('x.okr.objectives.no_items_description')}</p>
                </EmptyMessage>
              )}
            </MainContainerAnimation>
          )
        )}
        {props.objectivesListData && !loadingFilteredPage && (
          <Pagination
            t={t}
            totalItems={props.objectivesListData.pagination.total}
            pageSize={props.objectivesListData.pagination.per_page}
            onChange={getObjectivesOnPage}
            marginTop={60}
            history={props.history}
            location={props.location}
            defaultCurrPage={filterObj.page || 1}
          />
        )}
      </ListContainer>
    </>
  );
};

const mapStat2Props = ({ okrs }: StoreI): StateProps => ({
  loadingObjectives: okrs.objectives.loadingObjectives,
  objectivesListData: okrs.objectives.objectivesListData,
});

const mapDispatch2Props = (dispatch: any): DispatchProps => ({
  fetchObjectives: (queryObj) => dispatch(fetchObjectivesA(queryObj)),
});

export default connect(mapStat2Props, mapDispatch2Props)(ObjectivesOverview);
